import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { ShieldCheck, Truck } from "lucide-react";

function getUtm() {
  try {
    const raw = localStorage.getItem("asg_utm");
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

const CATEGORIES = [
  { key: "sigorta", label: "Sigorta & Kasko", icon: ShieldCheck },
  { key: "turbo_asist", label: "Turbo Asist", icon: Truck },
];

export default function RequestFormDialog({ open, onOpenChange, initialProductKey }) {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [category, setCategory] = useState("sigorta");
  const [selectedKey, setSelectedKey] = useState(initialProductKey || "");
  const [staffId, setStaffId] = useState("");
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/products").then((r) => {
      setProducts(r.data);
      // Determine initial category from initialProductKey
      if (initialProductKey) {
        const p = r.data.find((x) => x.key === initialProductKey);
        if (p) setCategory(p.category || "sigorta");
      }
    }).catch(() => {});
    api.get("/staff/public").then((r) => setStaff(r.data)).catch(() => {});
    setSelectedKey(initialProductKey || "");
    setValues({});
    setStaffId("");
  }, [open, initialProductKey]);

  const filteredProducts = useMemo(
    () => products.filter((p) => (p.category || "sigorta") === category),
    [products, category]
  );

  const product = useMemo(() => products.find((p) => p.key === selectedKey), [products, selectedKey]);
  const setField = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const changeCategory = (c) => { setCategory(c); setSelectedKey(""); setValues({}); };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedKey) { toast.error("Lütfen bir ürün seçin"); return; }
    if (product) {
      for (const f of product.form_fields || []) {
        if (f.required && !values[f.name]) { toast.error(`${f.label} zorunludur`); return; }
      }
    }
    setSubmitting(true);
    try {
      await api.post("/requests", {
        product_key: selectedKey,
        staff_id: staffId || null,
        data: values,
        utm: getUtm(),
      });
      try {
        if (settings?.google_ads_id && settings?.google_ads_conversion_label && window.gtag) {
          window.gtag("event", "conversion", { send_to: `${settings.google_ads_id}/${settings.google_ads_conversion_label}` });
        }
        if (window.gtag) window.gtag("event", "generate_lead", { product: selectedKey });
      } catch {}
      toast.success("Talebiniz alındı. En kısa sürede sizi arayacağız.");
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white border-[color:var(--asg-border)] text-[color:var(--asg-text)] max-h-[90vh] overflow-y-auto" data-testid="request-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Sigorta Talep Formu</DialogTitle>
          <DialogDescription className="text-[color:var(--asg-muted)]">
            İhtiyacınız olan ürünü seçin, size özel teklif hazırlayalım.
          </DialogDescription>
        </DialogHeader>

        {/* Category tabs */}
        <div className="grid grid-cols-2 rounded-lg border border-[color:var(--asg-border)] overflow-hidden mb-4" data-testid="dialog-category-tabs">
          {CATEGORIES.map((c) => {
            const active = category === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => changeCategory(c.key)}
                className={`flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all
                  ${active ? "text-[color:var(--asg-text)]" : "text-[color:var(--asg-muted)] hover:text-[color:var(--asg-text)]"}`}
                style={active ? { background: "linear-gradient(180deg, #ffffff, #fdf7e6)" } : {}}
                data-testid={`dialog-tab-${c.key}`}
              >
                <c.icon className={`w-4 h-4 ${active ? "asg-primary" : ""}`} />
                <span>{c.label}</span>
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="grid gap-5 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label className="text-[color:var(--asg-muted)]">
                {category === "turbo_asist" ? "Hizmet Türü" : "Sigorta Türü"} <span className="asg-primary">*</span>
              </Label>
              <Select value={selectedKey} onValueChange={setSelectedKey}>
                <SelectTrigger className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]" data-testid="request-product-select">
                  <SelectValue placeholder={filteredProducts.length ? "Ürün seçin" : "Bu sekmede ürün yok"} />
                </SelectTrigger>
                <SelectContent className="bg-white border-[color:var(--asg-border)]">
                  {filteredProducts.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`req-product-${p.key}`}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label className="text-[color:var(--asg-muted)]">Görüşmek İstediğiniz Personel</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <SelectTrigger className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]" data-testid="request-staff-select">
                  <SelectValue placeholder="Fark etmez" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[color:var(--asg-border)]">
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {product && (
            <div className="grid sm:grid-cols-2 gap-4">
              {(product.form_fields || []).map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2 grid gap-2" : "grid gap-2"}>
                  <Label className="text-[color:var(--asg-muted)]">
                    {f.label} {f.required && <span className="asg-primary">*</span>}
                  </Label>
                  {f.type === "textarea" ? (
                    <Textarea value={values[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)}
                      className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]"
                      data-testid={`req-field-${f.name}`} />
                  ) : f.type === "select" ? (
                    <Select value={values[f.name] || ""} onValueChange={(v) => setField(f.name, v)}>
                      <SelectTrigger className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]" data-testid={`req-field-${f.name}`}>
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[color:var(--asg-border)]">
                        {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={f.type || "text"} value={values[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)}
                      className="bg-[color:var(--asg-surface)] border-[color:var(--asg-border)] text-[color:var(--asg-text)]"
                      data-testid={`req-field-${f.name}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          <Button type="submit" disabled={submitting} className="asg-btn-primary rounded-full h-12 text-base font-semibold border-0" data-testid="request-submit-btn">
            {submitting ? "Gönderiliyor..." : "Teklif Talep Et"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
